import { Star, ExternalLink } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { BulletList, HostNote, PromptCard } from "./parts";
import { sessionLabel } from "./format";
import { isJoinable } from "./runningStatus";
import { useRunningStatus } from "./useRunningStatus";
import type { ClassroomSession } from "./types";

// The two tabs beside the receipt: "Get ready" (the host's pre-class note,
// what you'll learn and need, a bundle's chosen dates) and "Post event
// notes" (the host's note afterwards and the review prompt). Ports of v1's
// GetReadyTab and PostClassTab.

/** A date of an online session's join link: live from ten minutes before. */
export function JoinLink({ session, compact = false }: { session: ClassroomSession; compact?: boolean }) {
  const status = useRunningStatus(session);
  const url = session.onlineProviderMeeting?.join_url;
  if (!isJoinable(status) || !url) {
    return (
      <p className="text-xs italic text-muted-foreground">
        {compact ? "Join link available 10 min before the event" : "A join link will be available 10 min before the event starts"}
      </p>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80"
    >
      <ExternalLink className="h-3.5 w-3.5" />
      Join event link
    </a>
  );
}

export function GetReadyTab(p: {
  title: string;
  notes?: string;
  hostName?: string;
  hostAvatar?: string;
  whatYouWillLearn: string[];
  whatYouWillNeed: string[];
  bundleSessions: ClassroomSession[];
  timezone?: string;
}) {
  return (
    <div className="space-y-8 pt-6">
      <HostNote title={p.title} text={p.notes} name={p.hostName} avatar={p.hostAvatar} />
      {(p.whatYouWillLearn.length > 0 || p.whatYouWillNeed.length > 0) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {p.whatYouWillLearn.length > 0 && <BulletList title="What you will learn" items={p.whatYouWillLearn} />}
          {p.whatYouWillNeed.length > 0 && <BulletList title="What you will need" items={p.whatYouWillNeed} />}
        </div>
      )}
      {p.bundleSessions.length > 0 && (
        <section>
          <h2 className="mb-4 text-xl font-semibold">Selected dates of your bundle</h2>
          <ul className="space-y-3">
            {p.bundleSessions.map((s, i) => (
              <li key={s._id ?? i} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 p-4">
                <span className="font-medium">{sessionLabel(s, { timezone: p.timezone, isOnline: s.isOnline })}</span>
                {s.isOnline && <JoinLink session={s} />}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function PostClassTab(p: { title: string; notes?: string; hostName?: string; hostAvatar?: string; reviewHref: string | null }) {
  return (
    <div className="space-y-8 pt-6">
      <HostNote title={p.title} text={p.notes} name={p.hostName} avatar={p.hostAvatar} />
      {p.reviewHref && (
        <PromptCard
          title="Leave a review"
          icon={<Star className="h-5 w-5" />}
          text="We'd love to hear how it went! Share your thoughts on the event and drop some feedback for the organiser below."
          action={
            <Button asChild>
              <a href={p.reviewHref}>Leave a review</a>
            </Button>
          }
        />
      )}
    </div>
  );
}
